import { FC, useMemo } from 'react';
import { BpSubpage } from '../../../components/BpSubpage/BpSubpage';
import { BpCard } from '../../../components/BpCard/BpCard';
import { LazyLoader } from '@bp/ui-components';
import { NavigationTabs, NavigationTabsType } from '../../../components/NavigationTabs/NavigationTabs';
import { Outlet, useMatches } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { backendApi } from '../../../utils/backendApi';
import { showErrorToast } from '../../../utils/showErrorToast';
import { useQuery as useReactQuery } from '@tanstack/react-query';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { ProcuratOrganizationTable } from 'components/Procurat/tables/ProcuratOrganizationTable';
import { graphql } from 'client/gql';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { useQuery } from 'urql';

const SettingsProcuratSubpagePageQueryDocument = graphql(`
  query SettingsProcuratSubpagePageData($profile: ID!) {
    ...PermissionCheckerFields
  }
`);

export const SettingsProcuratSubpage: FC = () => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const organizationUuid = pimAuthClaims.getOrganizationUuid();
  const profileUuid = pimAuthClaims.getProfile().uuid;

  const [{ data: permissionData }] = useQuery({
    query: SettingsProcuratSubpagePageQueryDocument,
    variables: { profile: profileUuid },
  });
  const perms = usePermissionChecker(permissionData);

  const matches = useMatches();
  const last = [...matches].pop();

  const { data, refetch, isFetching } = useReactQuery({
    queryKey: ['procuratOrga'],
    queryFn: async () => {
      const response = await backendApi.fetch(`/procurat/organization/${import.meta.env.VITE_APP_PROCURAT_ENV}`, {
        method: 'GET',
      });
      return await response.json();
    },
  });

  if (data?.error) {
    showErrorToast({ graphQLErrors: [], message: data.error, name: '' });
  }

  const procuratOrga = data?.find((o: { current: boolean }) => o.current);

  const handleSetup = async (procuratOrgaId: string) => {
    const response = await backendApi.post(
      `/procurat/setup`,
      JSON.stringify({
        procuratEnvironment: import.meta.env.VITE_APP_PROCURAT_ENV,
        procuratOrganizationUuid: procuratOrgaId,
        organizationUuid: organizationUuid,
      }),
    );

    if (!response.ok) {
      showErrorToast({ graphQLErrors: [], message: response.statusText, name: '' });
    } else {
      await refetch();
    }
  };

  const tabs: NavigationTabsType[] = [
    {
      title: `${t('procuratImport.department.plural')}`.trim(),
      path: `/settings/procurat/`,
      active: last?.id === 'departments',
    },
    {
      title: `${t('schoolyears.title')}/${t('classes.titlePlural')}`.trim(),
      path: `classes`,
    },
    {
      title: `${t('groups.title')}`.trim(),
      path: `persongroups`,
    },
  ];

  return (
    <BpSubpage isForbidden={!perms?.canAccessProcuratImport()}>
      {isFetching ? (
        <LazyLoader embedded forceHeight='15vh' />
      ) : !perms?.isOmniAdmin() ? (
        !data?.error && procuratOrga ? (
          <>
            <h2>{procuratOrga?.name}</h2>
          </>
        ) : (
          <>
            <h2>{t('procuratImport.notEnabledOrNoConnection')}</h2>
          </>
        )
      ) : (
        <BpCard
          header={{
            headline: t('procuratImport.organisations'),
            subHeadline: procuratOrga?.name,
            showSubHeadlineAsChip: true,
          }}
          noPadding
          alwaysCollapsible={true}
          isCollapsed={true}
        >
          <ProcuratOrganizationTable data={data} onSetup={handleSetup} />
        </BpCard>
      )}
      {!data?.error && procuratOrga && (
        <div className='mt-8'>
          <NavigationTabs tabs={tabs} size='s' className='mb-4' />
          <Outlet />
        </div>
      )}
    </BpSubpage>
  );
};
